import React, { useRef } from "react";
import { useReveal } from "../hooks/useReveal";
import { useSEO } from "../hooks/useSEO";

const ARTICLES = [
  {
    numeral: "I",
    title: "L'humour est un art.",
    body: [
      "Pendant deux décennies, une génération d'artistes a silencieusement recomposé le paysage comique francophone. De nouvelles formes sont nées — le one-man-show structuré comme une pièce de théâtre, le podcast d'humour monté avec la rigueur d'un documentaire, la sketch-série écrite avec la précision d'un scénario de cinéma. De nouvelles voix, venues de partout dans l'espace francophone, ont imposé leur langue, leur regard, leur sensibilité.",
      "Et pourtant : aucune institution ne les reconnaissait. Aucun prix, en langue française, ne s'était fixé comme mission de distinguer, de cartographier, d'archiver. Aucun jury indépendant ne délibérait sur leurs mérites avec le sérieux que l'on accorde, ailleurs, au roman, au film ou à l'opéra.",
      "Le Stand d'Or naît de ce constat. Non pas pour combler un vide — les artistes n'avaient pas besoin de nous. Mais pour poser, une fois pour toutes, une affirmation que nous tenons pour évidente : l'humour est un art. Il mérite une institution.",
    ],
  },
  {
    numeral: "II",
    title: "Contre la hiérarchie des genres.",
    body: [
      "Il existe, dans la culture française, une hiérarchie implicite des formes artistiques. En bas de cette hiérarchie, depuis toujours, la comédie. Tolérée, consommée en masse, rarement critiquée, jamais vraiment célébrée. Les Césars ont une catégorie « meilleur film de comédie » — mais l'humour pur, le stand-up, la plume comique, n'ont aucun équivalent.",
      "Cette hiérarchie est fausse. Elle est le produit d'un préjugé, d'une confusion entre le rire facile et le rire exigeant, entre le divertissement mécanique et la comédie comme forme critique du monde.",
      "Molière était un comique. Feydeau était un comique. Desproges était un comique. Ils ont tous, à leur manière, fait trembler quelque chose — dans les certitudes de leur époque, dans les conventions de leur temps, dans la conscience de leurs spectateurs. Ce que fait le meilleur stand-up contemporain n'est pas différent. Il est seulement plus jeune.",
    ],
  },
  {
    numeral: "III",
    title: "Pourquoi un jury.",
    body: [
      "Nous aurions pu faire un prix du public. C'était la voie la plus simple, la plus visible, la plus commerciale. Nous avons choisi l'inverse.",
      "Un jury indépendant, composé de neuf personnalités issues de la mise en scène, de la programmation, de la critique, de l'écriture. Une présidence rotative. Des délibérations à huis clos. Un vote à bulletin unique. Pas de pression des plateformes, pas d'influence des agents, pas de campagne de lobbying.",
      "La légitimité d'une institution se construit par ses règles, non par son audience. Un prix qui désigne le plus populaire est une étude de marché. Un prix qui désigne le meilleur est une affirmation critique. Nous voulons faire la seconde chose.",
      "Le prix du public existe en complément — nous ne méprisons pas le suffrage universel. Mais nous croyons que la critique indépendante a une fonction irremplaçable : nommer ce qui mérite d'être nommé, même quand personne ne regarde encore.",
    ],
  },
  {
    numeral: "IV",
    title: "Treize catégories.",
    body: [
      "Nous avons refusé de réduire l'humour à une seule forme. Le Stand d'Or distingue treize disciplines — de la Révélation de l'Année au Grand Prix, en passant par la Meilleure Plume, le Meilleur Podcast, l'Humoriste Digital, la Meilleure Sketch-Série.",
      "Chaque catégorie a été pensée pour répondre à une réalité du champ contemporain. Le stand-up de scène et l'écriture numérique ne s'évaluent pas avec les mêmes critères. Un spectacle de 90 minutes et une vidéo de 8 minutes obéissent à des logiques différentes. Les distinguer, c'est les respecter.",
      "La cartographie n'est pas définitive. Elle évoluera, édition après édition, avec le champ lui-même. Nous ne prétendons pas avoir tout prévu. Nous prétendons avoir regardé avec soin.",
    ],
  },
  {
    numeral: "V",
    title: "La francophonie comme territoire.",
    body: [
      "Le Stand d'Or n'est pas un prix français. C'est un prix francophone. La nuance est décisive.",
      "Paris a longtemps capté l'essentiel de l'attention, de l'industrie, du récit. Mais l'humour francophone vit aussi à Montréal, à Bruxelles, à Lausanne, à Dakar, à Abidjan, à Alger, à Tunis. Ces scènes ont leurs codes, leurs influences, leurs voix propres. Elles méritent d'être intégrées à un récit commun.",
      "Notre seule frontière est la langue. Et c'est la seule que nous reconnaissons.",
    ],
  },
  {
    numeral: "VI",
    title: "L'indépendance comme condition.",
    body: [
      "Un prix n'est crédible que s'il est indépendant. Indépendant des diffuseurs, qui ont des intérêts dans certains artistes. Indépendant des plateformes, qui ont des algorithmes à nourrir. Indépendant des agents, qui ont des carrières à gérer.",
      "Le Stand d'Or se finance par des partenariats institutionnels et des marques dont les valeurs sont compatibles avec les nôtres. Ces partenaires soutiennent l'institution — ils n'interviennent pas dans les délibérations. Cette règle est absolue et non négociable.",
      "Nous savons que l'indépendance se proclame facilement et se perd discrètement. Nous nous engageons à publier, chaque année, la liste complète de nos partenaires et les conditions de leur soutien. La transparence est notre seule garantie.",
    ],
  },
  {
    numeral: "VII",
    title: "Ce que nous espérons.",
    body: [
      "Dans dix ans, nous espérons que Le Stand d'Or sera une référence. Que ses palmarès seront consultés, discutés, contestés. Que ses lauréats seront reconnus pour ce qu'ils sont — les meilleurs de leur génération dans l'art qui est le leur.",
      "Nous espérons que cette institution aura contribué à modifier, même légèrement, la façon dont la société française et francophone regarde l'humour. Qu'elle aura prouvé qu'un texte comique peut être analysé avec la même rigueur qu'un texte dramatique. Qu'elle aura donné à une génération d'artistes la reconnaissance que leur travail mérite.",
      "Nous espérons, enfin, que dans dix ans, l'idée qu'il n'existait pas de prix de l'humour francophone semblera aussi étrange que l'idée qu'il n'existait pas de prix du cinéma.",
      "Nous commençons maintenant.",
    ],
  },
];

function Article({ article, index }) {
  const ref = useReveal();
  return (
    <article
      ref={ref}
      className="reveal"
      style={{ borderTop: "1px solid #1c1c1c", paddingTop: 80, paddingBottom: 80 }}
      data-testid={`manifeste-article-${index + 1}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-3 lg:col-span-3">
          <p
            className="font-serif"
            style={{
              fontSize: "clamp(48px, 6vw, 88px)",
              color: "rgba(201,169,97,0.18)",
              lineHeight: 1,
              letterSpacing: "-0.025em",
              fontStyle: "italic",
              position: "sticky",
              top: 140,
            }}
            aria-hidden="true"
          >
            {article.numeral}
          </p>
        </div>
        <div className="md:col-span-8 lg:col-span-8 md:col-start-5">
          <h2
            className="font-serif mb-12"
            style={{ fontSize: "clamp(28px, 3.5vw, 48px)", lineHeight: 1.1, color: "#F8F6F0", letterSpacing: "-0.015em" }}
          >
            {article.title}
          </h2>
          <span className="manifeste-hairline block mb-12" style={{ width: 64 }} />
          <div className="space-y-7">
            {article.body.map((para, i) => (
              <p
                key={i}
                style={{
                  color: i === 0 ? "rgba(248,246,240,0.92)" : "rgba(248,246,240,0.68)",
                  fontSize: 17,
                  lineHeight: 1.88,
                }}
              >
                {para}
              </p>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}

export default function About() {
  useSEO(
    "Manifeste",
    "L'humour francophone mérite une institution. Le manifeste fondateur du Stand d'Or — pourquoi ce prix, ces règles, cette ambition.",
  );

  const preambuleRef = useReveal();
  const declarationRef = useReveal();
  const signataireRef = useReveal();

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden grain"
        style={{ minHeight: "100vh", background: "#000", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}
        data-testid="manifeste-hero"
        aria-label="Manifeste — Le Stand d'Or"
      >
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=2000)",
            filter: "grayscale(100%) brightness(0.22)",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.45) 50%, #000 100%)" }}
          aria-hidden="true"
        />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 pt-[200px] w-full">
          <p
            className="label manifeste-fade"
            style={{ color: "#C9A961", marginBottom: 40 }}
          >
            Le Stand d'Or — Manifeste fondateur
          </p>

          <h1
            className="font-serif manifeste-title"
            style={{
              fontSize: "clamp(64px, 13vw, 200px)",
              lineHeight: 0.88,
              letterSpacing: "-0.035em",
              color: "#F8F6F0",
            }}
            data-testid="manifeste-title"
          >
            L'humour entre
            <br />
            <span style={{ fontStyle: "italic", color: "#C9A961" }}>dans sa légende.</span>
          </h1>

          <div className="mt-16 flex items-center gap-8 manifeste-sub">
            <span className="manifeste-hairline" style={{ width: 80, flexShrink: 0 }} />
            <p style={{ color: "rgba(248,246,240,0.65)", fontSize: 16, lineHeight: 1.6, maxWidth: 480 }}>
              Fondé en 2026. Première institution dédiée aux awards de l'humour francophone.
            </p>
          </div>
        </div>
      </section>

      {/* PRÉAMBULE */}
      <section
        ref={preambuleRef}
        className="reveal"
        style={{ borderTop: "1px solid #1c1c1c" }}
        data-testid="manifeste-preambule"
      >
        <div
          className="mx-auto px-6 lg:px-12 py-32 md:py-48"
          style={{ maxWidth: 800 }}
        >
          <p className="label mb-10" style={{ color: "#C9A961" }}>Préambule</p>
          <p
            className="font-serif"
            style={{
              color: "#F8F6F0",
              fontSize: "clamp(19px, 2vw, 24px)",
              lineHeight: 1.78,
            }}
          >
            <span
              className="font-serif"
              style={{
                float: "left",
                fontSize: "clamp(88px, 12vw, 140px)",
                lineHeight: 0.82,
                paddingRight: 18,
                marginTop: 10,
                color: "#C9A961",
                fontStyle: "italic",
              }}
              aria-hidden="true"
            >
              L
            </span>
            e rire est une affaire sérieuse. Il ne s'excuse pas d'être efficace. Il ne s'excuse pas d'être populaire.
            Il s'excuse encore moins de donner du plaisir — c'est même, à bien y réfléchir, sa définition. Ce qui le
            distingue d'un simple divertissement, c'est l'art avec lequel il y parvient. La langue. La structure. Le
            point de vue. La vérité qu'il dit en faisant semblant de ne pas la dire.
          </p>
          <p
            className="mt-10"
            style={{ color: "rgba(248,246,240,0.72)", fontSize: "clamp(17px, 1.7vw, 20px)", lineHeight: 1.85 }}
          >
            Ce manifeste n'est pas un programme politique. Ce n'est pas non plus une déclaration d'intention floue.
            C'est l'exposé des raisons pour lesquelles nous avons fondé cette institution — et des règles auxquelles
            nous nous engageons à la soumettre. On peut ne pas être d'accord avec nos choix. On ne peut pas nous
            reprocher de ne pas les avoir explicités.
          </p>
        </div>
      </section>

      {/* SEPT ARTICLES */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12" data-testid="manifeste-articles">
        {ARTICLES.map((article, i) => (
          <Article key={article.numeral} article={article} index={i} />
        ))}
      </div>

      {/* DÉCLARATION FINALE */}
      <section
        ref={declarationRef}
        className="reveal"
        style={{ background: "#000", borderTop: "1px solid #1c1c1c", borderBottom: "1px solid #1c1c1c" }}
        data-testid="manifeste-declaration"
      >
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-40 md:py-64">
          <p
            className="font-serif"
            style={{
              fontSize: "clamp(32px, 5vw, 76px)",
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: "#F8F6F0",
              fontStyle: "italic",
              maxWidth: 960,
            }}
          >
            « Nous ne faisons pas un prix de l'humour.
            <br />
            Nous faisons un prix qui prend
            <br />
            <span style={{ color: "#C9A961" }}>l'humour au sérieux.</span> »
          </p>
          <span className="block mt-16 mb-16" style={{ width: 64, height: 1, background: "#C9A961", opacity: 0.7 }} />
          <p
            style={{ color: "rgba(248,246,240,0.6)", fontSize: 16, lineHeight: 1.75, maxWidth: 560 }}
          >
            La distinction entre les deux est toute l'histoire de ce projet. Elle résume notre philosophie, notre
            méthode, notre ambition. L'humour est sérieux parce qu'il compte. Il compte parce qu'il dit vrai.
            Il dit vrai parce que des artistes y consacrent leur vie.
          </p>
        </div>
      </section>

      {/* SIGNATAIRES */}
      <section
        ref={signataireRef}
        className="reveal max-w-[1400px] mx-auto px-6 lg:px-12 py-32 md:py-48"
        data-testid="manifeste-signataires"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-4">
            <p className="label mb-8" style={{ color: "#C9A961" }}>Signataires</p>
            <h2
              className="font-serif"
              style={{ fontSize: "clamp(36px, 4.5vw, 64px)", lineHeight: 1.05, color: "#F8F6F0", letterSpacing: "-0.02em" }}
            >
              Ceux qui s'engagent.
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <div style={{ borderTop: "1px solid #1c1c1c", paddingTop: 40 }}>
              <p className="label mb-3" style={{ color: "#A1A1AA" }}>Fondateur</p>
              <p className="font-serif" style={{ fontSize: 28, color: "#F8F6F0", lineHeight: 1.2 }}>Guepster</p>
              <p className="label mt-2" style={{ color: "#A1A1AA" }}>Le Stand d'Or, Paris — 2026</p>
            </div>
            <div style={{ borderTop: "1px solid #1c1c1c", marginTop: 40, paddingTop: 40 }}>
              <p className="label mb-3" style={{ color: "#A1A1AA" }}>Le Jury 2026</p>
              <p style={{ color: "rgba(248,246,240,0.65)", fontSize: 16, lineHeight: 1.75 }}>
                Neuf membres indépendants, nommés pour l'édition inaugurale. Leur identité sera révélée lors de
                l'annonce des nominations.
              </p>
            </div>
            <div style={{ borderTop: "1px solid #1c1c1c", marginTop: 40, paddingTop: 40 }}>
              <p className="label mb-3" style={{ color: "#A1A1AA" }}>Les nominés 2026</p>
              <p style={{ color: "rgba(248,246,240,0.65)", fontSize: 16, lineHeight: 1.75 }}>
                Par leur acceptation de la nomination, chaque artiste sélectionné souscrit aux principes de ce
                manifeste et à l'indépendance du jury.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* COLOPHON */}
      <div
        className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 flex flex-wrap items-center justify-between gap-8"
        style={{ borderTop: "1px solid #1c1c1c" }}
        data-testid="manifeste-colophon"
      >
        <p className="label" style={{ color: "rgba(248,246,240,0.35)", letterSpacing: "0.12em" }}>
          Fait à Paris, l'an&nbsp;MMXXVI
        </p>
        <button
          className="label"
          style={{
            color: "#C9A961",
            border: "1px solid rgba(201,169,97,0.4)",
            padding: "10px 20px",
            background: "transparent",
            cursor: "pointer",
            letterSpacing: "0.1em",
          }}
          onClick={() => window.print()}
          aria-label="Télécharger le manifeste en PDF"
        >
          Télécharger le manifeste →
        </button>
      </div>
    </div>
  );
}
